<template>
  <BaseTextInput
    :id="id"
    :disabled="disabled"
    :full-width="fullWidth"
    :helper-text-id="helperTextId"
    :hide-label="hideLabel"
    :input-classes="inputClasses"
    :label="label"
    :label-classes="labelClasses"
    :autocomplete="autocomplete"
    :name="name"
    :placeholder="placeholder"
    :required="required"
    :show-error-message="errorMessage.includes('required') || errorMessage.includes('match')"
    :type="showPassword ? 'text' : 'password'"
    :validation-rules="validationRules"
    @blur="capsLockIsEnabled = false"
  >
    <template #icon>
      <button
        class="wc-focus rounded-sm"
        data-test-id="toggle-password-visibility-button"
        type="button"
        @click="showPassword = !showPassword"
      >
        <span
          class="sr-only"
          v-text="showPassword ? 'Hide Password' : 'Show Password'"
        />
        <component
          :is="showPassword ? IconEye : IconEyeClosed"
          :size="24"
          aria-hidden="true"
        />
      </button>
    </template>

    <template
      v-if="capsLockIsEnabled"
      #helperText
    >
      <p class="flex items-center gap-2 text-red">
        <IconAlertCircleFilled
          :size="20"
          aria-hidden="true"
        />
        <span v-text="'Caps lock is turned on.'" />
      </p>
    </template>
  </BaseTextInput>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { IconAlertCircleFilled, IconEye, IconEyeClosed } from '@tabler/icons-vue'

import BaseTextInput from '@js/components/global/forms/inputs/BaseTextInput.vue'
import { HtmlAutocomplete } from '@js/types/HtmlAutocomplete'

defineOptions({
  name: 'BasePasswordInput',
})

const props = withDefaults(
  defineProps<{
    id: string
    name: string
    label: string
    disabled?: boolean
    required?: boolean
    fullWidth?: boolean
    hideLabel?: boolean
    placeholder?: string
    helperTextId?: string
    validationRules?: string
    labelClasses?: string | null
    autocomplete?: HtmlAutocomplete
    errorMessage?: string | undefined
    inputClasses?: string | string[] | null
  }>(),
  {
    autocomplete: 'current-password',
    disabled: false,
    fullWidth: true,
    placeholder: '',
    required: false,
    errorMessage: '',
    helperTextId: '',
    hideLabel: false,
    inputClasses: null,
    labelClasses: null,
    validationRules: 'required|password',
  },
)

defineModel<string>({
  default: '',
})

const showPassword = ref(false)
const capsLockIsEnabled = ref(false)

const setCapsLockState = (event: KeyboardEvent | MouseEvent): void => {
  if (!event.getModifierState) {
    return
  }

  capsLockIsEnabled.value = event.getModifierState('CapsLock')
}

onMounted(() => {
  const input = document.getElementById(props.id)

  if (input) {
    input.addEventListener('click', setCapsLockState)
    input.addEventListener('keyup', setCapsLockState)
    input.addEventListener('keydown', setCapsLockState)
  }
})

onBeforeUnmount(() => {
  const input = document.getElementById(props.id)

  if (input) {
    input.removeEventListener('click', setCapsLockState)
    input.removeEventListener('keyup', setCapsLockState)
    input.removeEventListener('keydown', setCapsLockState)
  }
})
</script>
